import { useHistory, useLocation } from 'react-router-dom'
import { reduxForm, initialize, getFormValues, SubmissionError } from 'redux-form'
import { Fade, Error, Loader, StreetAddress } from '../../components/common'
import { fetchContactInformation } from '../../actions/settings'
import {
  fetchReshipmentSummary,
  fetchSavedReshipmentItems,
  fetchNonSecureMaterials,
  submitReshipmentOrder,
} from '../../actions/reshipment'
import {
  ReshipmentSummaryForTestWindow,
  AddNewMaterialButton,
  NewReshipmentsSection,
  ApprovedReshipmentsSection,
} from '../../components/reshipment'
import { NAV_LINKS } from '../../constants/NavConstants'
import { RESHIPMENT_FORM_NAME, RESHIPMENT_QTY_FIELD } from '../../constants/ReshipmentConstants'
import { setNavLink, unsetNavLink, suppressNav, unSuppressNav } from '../../actions/nav'
import {
  getReshipmentSummaryData,
  getReshipmentSummaryStatusProps,
  getNonSecureMaterials,
  getNonSecureMaterialsStatusProps,
} from '../../selectors/reshipment'
import { selectExamDateRefData } from '../../selectors/examWindows'
import { isEmpty, queryParamsToObj, convertTitleCase } from '../../utils/common'
import { formatPhoneNumber } from '../../utils/numbers'

const mapStateToProps = state => {
  const { fetching: fetchingContactInfo, contactInfo } = state.orgContactInformation
  const { error: summaryError } = getReshipmentSummaryStatusProps(state)
  const { data: summaryData } = getReshipmentSummaryData(state)
  const { data: nonSecureMaterials } = getNonSecureMaterials(state)
  const examDateRefData = selectExamDateRefData(state)
  const {
    fetching: fetchingNonSecureMaterials,
    fetched: fetchedNonSecureMaterials,
    error: errorNonSecureMaterials,
  } = getNonSecureMaterialsStatusProps(state)
  const initialValues = {}
  const currentFormValues = getFormValues(RESHIPMENT_FORM_NAME)(state) || {}
  const testWindows = []
  examDateRefData.forEach(examDateType => {
    if (summaryData[examDateType]?.result?.length > 0) {
      testWindows.push(examDateType)
    }
  })
  const itemOrder = {}
  testWindows.forEach(testWindow => {
    initialValues[testWindow] = {}
    itemOrder[testWindow] = summaryData?.[testWindow]?.result
    summaryData?.[testWindow]?.result.forEach(id => {
      initialValues[testWindow][id] = {
        ...summaryData[testWindow].entities.items[id],
        [RESHIPMENT_QTY_FIELD]:
          summaryData[testWindow].entities.items[id][RESHIPMENT_QTY_FIELD] || 0,
      }
    })
  })
  return {
    contactInfo,
    fetchingContactInfo,
    fetchingNonSecureMaterials,
    fetchedNonSecureMaterials,
    errorNonSecureMaterials,
    nonSecureMaterials,
    summaryData,
    summaryError,
    itemOrder,
    testWindows,
    initialValues,
    currentFormValues,
  }
}

const ReshipmentPage = props => {
  const {
    contactInfo,
    fetchingContactInfo,
    fetchingNonSecureMaterials,
    fetchedNonSecureMaterials,
    errorNonSecureMaterials,
    nonSecureMaterials,
    summaryData,
    summaryError,
    itemOrder,
    testWindows,
    initialValues,
    currentFormValues,
    fetchContactInformation,
    fetchReshipmentSummary,
    fetchSavedReshipmentItems,
    fetchNonSecureMaterials,
    setNavLink,
    unsetNavLink,
    suppressNav,
    unSuppressNav,
  } = props
  const history = useHistory()
  const { search } = useLocation()
  const { orgId, aiCode } = queryParamsToObj(search)
  const shippingAddress = contactInfo?.[orgId]?.shippingAddress ?? {}
  const coordinatorContactInfo = contactInfo?.[orgId]?.coordinator ?? {}

  useEffect(() => {
    suppressNav()
    setNavLink(NAV_LINKS.orders, 'Back to search')
    return () => {
      unsetNavLink()
      unSuppressNav()
    }
  }, [])

  useEffect(() => {
    const fetchReshipmentData = async () => {
      await fetchReshipmentSummary(orgId)
      await fetchSavedReshipmentItems(orgId)
    }

    if (!isEmpty(orgId) && !isEmpty(aiCode)) {
      fetchContactInformation(orgId, aiCode)
      fetchReshipmentData()
      if (
        !Object.keys(nonSecureMaterials).length &&
        !fetchingNonSecureMaterials &&
        !fetchedNonSecureMaterials &&
        !errorNonSecureMaterials
      ) {
        fetchNonSecureMaterials()
      }
    } else {
      history.push(NAV_LINKS.orders)
    }
  }, [
    orgId,
    aiCode,
    nonSecureMaterials,
    fetchingNonSecureMaterials,
    fetchedNonSecureMaterials,
    errorNonSecureMaterials,
  ])

  useEffect(() => {
    initialize(RESHIPMENT_FORM_NAME, initialValues)
  }, [initialValues])

  return (
    <Fade>
      <div className="container">
        <div className="row">
          <div className="col-xs-8">
            <div className="row">
              <div className="col-xs-12">
                <h1 id="SchoolName" style={{ marginBottom: 0 }}>
                  {shippingAddress?.schoolName}
                </h1>
                <div id="SchoolAICode" style={{ marginBottom: '2em' }}>
                  AI Code: {aiCode}
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-xs-6">
                <h4 className="roboto-bold">School Address</h4>
                {fetchingContactInfo ? (
                  <Loader />
                ) : (
                  <>
                    <div id="AddrSchoolName">{shippingAddress?.schoolName}</div>
                    <StreetAddress {...shippingAddress} />
                  </>
                )}
              </div>
              <div className="col-xs-6">
                <h4 className="roboto-bold">AP Coordinator</h4>
                {fetchingContactInfo ? (
                  <Loader />
                ) : (
                  <>
                    <div id="CoordinatorName">
                      {coordinatorContactInfo?.firstName} {coordinatorContactInfo?.lastName}
                    </div>
                    <div id="CoordinatorEmail">{coordinatorContactInfo?.email}</div>
                    <div id="CoordinatorPhone">
                      {formatPhoneNumber(coordinatorContactInfo?.telephone)}
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-xs-12">
                <h2>Order Summary</h2>
              </div>
            </div>
            {summaryError ? (
              <div className="row">
                <div className="col-xs-12">
                  <Error />
                </div>
              </div>
            ) : (
              <form onSubmit={e => e.preventDefault()}>
                {testWindows.map(examDateType => {
                  if (summaryData[examDateType]?.result?.length > 0) {
                    return (
                      <div className="row" key={examDateType}>
                        <div className="col-xs-12" style={{ marginBottom: '1em' }}>
                          <h3 id={`${examDateType}-window-heading`} style={{ marginBottom: '5px' }}>
                            {convertTitleCase(examDateType)}
                          </h3>
                          <ReshipmentSummaryForTestWindow
                            data={currentFormValues[examDateType]}
                            testWindow={examDateType}
                            itemOrder={itemOrder[examDateType]}
                          />
                          <AddNewMaterialButton testWindow={examDateType} />
                        </div>
                      </div>
                    )
                  }
                  return null
                })}
              </form>
            )}
          </div>
          <div className="col-xs-4">
            <NewReshipmentsSection />
            <ApprovedReshipmentsSection />
          </div>
        </div>
      </div>
    </Fade>
  )
}

export default connect(mapStateToProps, {
  fetchContactInformation,
  fetchReshipmentSummary,
  fetchSavedReshipmentItems,
  fetchNonSecureMaterials,
  submitReshipmentOrder,
  setNavLink,
  unsetNavLink,
  suppressNav,
  unSuppressNav,
})(
  reduxForm({
    form: RESHIPMENT_FORM_NAME,
    enableReinitialize: true,
    onSubmit: (values, dispatch, { contactInfo, submitReshipmentOrder }) => {
      // orgId should be the key under which the contactInfo is nested
      const [orgId] = Object.keys(contactInfo)
      if (!orgId) {
        throw new SubmissionError('Error: No orgId provided')
      }
      return submitReshipmentOrder(orgId)
    },
  })(ReshipmentPage)
)
